import Calendar from "./Calendar";
import moment from "moment";
import { format } from "date-fns";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cloneElement, useCallback, useMemo, useState } from "react";
import { EventProps, Views } from "react-big-calendar";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Calendar as ShadcnCalendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button/button";

import { RESOURCES, VIEW_OPTIONS } from "./constants";
// import { EventItem } from "./types";
// import { AppointmentEvent } from "./appointment-event";
import {
  getAttendanceForEmployeeQueryOptions,
  getCurrentEmployeeCheckinsDayWiseQueryOptions,
} from "@/config/queries/emp-query-options";
import { convertUTCtoIST } from "@/utils";
import { AttendanceEvent } from "./attendance-event";
import { AttendanceEventItem } from "./att-types";
// import { useAuth } from "@/lib/use-auth";
import { HolidayEvent } from "./events/holiday-event";

type Keys = keyof typeof Views;

const mapLines = (nthChild: string, width: number) =>
  `.rbc-day-slot [&.rbc-time-slot:nth-child(${nthChild})]:after:w-[${width}]`;

export enum TimeSlotMinutes {
  Five = 5,
  Ten = 10,
  Fifteen = 15,
  Thirty = 30,
}

const timeSlotLinesMap = {
  [TimeSlotMinutes.Five]: `${mapLines("6n + 4", 25)} ${mapLines(
    "3n + 2",
    12.5
  )} ${mapLines("3n + 3", 12.5)}`,
  [TimeSlotMinutes.Ten]: `${mapLines("3n + 2", 12.5)} ${mapLines(
    "3n + 3",
    12.5
  )}`,
  [TimeSlotMinutes.Fifteen]: mapLines("2n", 25),
  [TimeSlotMinutes.Thirty]: "",
};

interface MyObject {
  _id: string;
  createdAt: string | Date; // Assuming ISO 8601 string format
  employee: string;
  mode: string;
}

function findObjectByDate(
  objects: MyObject[],
  mode: "Earliest" | "Latest"
): MyObject | null {
  if (objects.length === 0) return null;

  let resultObject: MyObject = objects[0];
  let resultDate: Date = new Date(resultObject.createdAt);

  for (let i = 1; i < objects.length; i++) {
    const currentDate: Date = new Date(objects[i].createdAt);
    if (mode === "Earliest" && currentDate < resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    } else if (mode === "Latest" && currentDate > resultDate) {
      resultObject = objects[i];
      resultDate = currentDate;
    }
  }

  return resultObject;
}

export const CustomizedCalendar = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [view, setView] = useState<(typeof Views)[Keys]>(Views.MONTH);

  const empKey = Cookies.get("key") as string;

  const todaysDate = useMemo(() => new Date(), []);

  const { data: EmployeeCheckins, isPending: isEmployeeCheckinsPending } =
    useQuery(getCurrentEmployeeCheckinsDayWiseQueryOptions(empKey, todaysDate));

  const todayCheckins = useMemo(() => {
    if (isEmployeeCheckinsPending) {
      return null;
    }

    if (
      EmployeeCheckins === undefined ||
      EmployeeCheckins?.checkins === undefined
    ) {
      return null;
    }

    const currentEmp = EmployeeCheckins?.checkins?.find((item) => {
      if (item?.mode === "IN") {
        return true;
      }

      return false;
    });

    return currentEmp;
  }, [EmployeeCheckins, isEmployeeCheckinsPending]);

  const datesSC = useMemo(() => {
    if (view === Views.DAY) {
      const startdate = convertUTCtoIST(
        new Date(new Date(date as Date).setHours(0))
      );
      const enddate = convertUTCtoIST(
        new Date(new Date(date as Date).setHours(23))
      );

      return [startdate, enddate];
    }

    if (view === Views.WEEK) {
      const from = moment(date)?.startOf("week").toDate();
      const to = moment(date)?.endOf("week").toDate();

      const startdate = convertUTCtoIST(
        new Date(new Date(from as Date).setHours(0))
      );

      const enddate = convertUTCtoIST(
        new Date(new Date(to as Date).setHours(23))
      );

      return [startdate, enddate];
    }

    if (view === Views.MONTH) {
      const from = moment(date)?.startOf("month").toDate();
      const to = moment(date)?.endOf("month").toDate();

      const startdate =
        // convertUTCtoIST(
        new Date(new Date(from as Date).setHours(0));
      // );

      const enddate =
        // convertUTCtoIST(
        new Date(new Date(to as Date).setHours(23));
      // );

      return [startdate, enddate];
    }
  }, [view, date]);

  const { data, isPending, isFetching } = useQuery(
    getAttendanceForEmployeeQueryOptions(
      empKey,
      datesSC?.[0] as Date,
      datesSC?.[1] as Date
    )
  );

  // const { data: authData } = useAuth();

  const ATTENDANCE_EVENTS = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.attendance === undefined) {
      return [];
    }

    const result = data?.attendance?.map((att) => {
      const from = moment(att?.date)?.toDate();
      const to = moment(att?.date)?.toDate();

      // console.log("Ätt", att);

      const checkin = findObjectByDate(att?.checkin, "Earliest");
      const checkout = findObjectByDate(att?.checkout, "Latest");

      // console.log({
      //   checkin,
      //   checkout,
      // });

      return {
        start: from,
        end: to,
        data: {
          attendance: {
            id: att?._id,
            createdAt: att?.createdAt,
            status: att?.status,
            date: att?.date,
            ...(checkin !== null && checkin?.createdAt
              ? {
                  checkinTime: format(
                    new Date(checkin?.createdAt as string),
                    "p"
                  ),
                }
              : {
                  checkinTime: "",
                }),
            ...(checkout !== null && checkout?.createdAt
              ? {
                  checkoutTime: format(
                    new Date(checkout?.createdAt as string),
                    "p"
                  ),
                }
              : {
                  checkoutTime: "",
                }),
            // checkinTime: format(new Date(checkin?.createdAt as string), "PPpp"),
            // checkoutTime: format(
            //   new Date(checkout?.createdAt as string),
            //   "PPpp"
            // ),
          },
        },
      };
    });

    // const holidays = authData?.user?.holidayList?.holidays;

    // // @ts-ignore
    // const holidaysMarking = holidays?.map((holiday) => {
    //   const from = moment(holiday?.date)?.toDate();
    //   const to = moment(holiday?.date)?.toDate();

    //   return {
    //     start: from,
    //     end: to,
    //     data: {
    //       holiday: {
    //         status: holiday?.holidayName,
    //         date: holiday?.date,
    //       },
    //     },
    //   };
    // });

    return [
      ...result,
      ...(todayCheckins
        ? [
            {
              start: new Date(),
              end: new Date(),
              data: {
                attendance: {
                  status: "Present",
                  checkinTime: format(
                    new Date(todayCheckins?.createdAt as string),
                    "p"
                  ),
                },
              },
            },
          ]
        : [
            {
              start: new Date(),
              end: new Date(),
              data: {
                attendance: {
                  status: "Absent",
                  checkinTime: "",
                },
              },
            },
          ]),
      // ...holidaysMarking
    ];
  }, [data, isPending, isFetching, todayCheckins]);

  // console.log("Attendance Events", ATTENDANCE_EVENTS);

  // @ts-ignore
  const [contextMenuInfo, setContextMenuInfo] = useState<{
    xPosition: number;
    yPosition: number;
    selectedTime: string;
    resourceId: number;
  }>();

  const onTodayClick = useCallback(() => {
    setDate(moment().toDate());
  }, []);

  const onPrevClick = useCallback(() => {
    if (view === Views.DAY) {
      setDate(moment(date).subtract(1, "d").toDate());
    } else if (view === Views.WEEK) {
      setDate(moment(date).subtract(1, "w").toDate());
    } else {
      setDate(moment(date).subtract(1, "M").toDate());
    }
  }, [view, date]);

  const onNextClick = useCallback(() => {
    if (view === Views.DAY) {
      setDate(moment(date).add(1, "d").toDate());
    } else if (view === Views.WEEK) {
      setDate(moment(date).add(1, "w").toDate());
    } else {
      setDate(moment(date).add(1, "M").toDate());
    }
  }, [view, date]);

  const dateText = useMemo(() => {
    if (view === Views.DAY) return moment(date).format("dddd, MMMM DD");
    if (view === Views.WEEK) {
      const from = moment(date)?.startOf("week");
      const to = moment(date)?.endOf("week");
      return `${from.format("MMMM DD")} to ${to.format("MMMM DD")}`;
    }
    if (view === Views.MONTH) {
      return moment(date).format("MMMM YYYY");
    }
  }, [view, date]);

  const components: any = {
    event: ({ event }: EventProps<AttendanceEventItem>) => {
      const data = event?.data;
      if (data?.attendance)
        return (
          <AttendanceEvent attendance={data?.attendance} />
          // <AppointmentEvent
          //   appointment={data?.appointment}
          //   isMonthView={view === Views.MONTH}
          // />
        );

      if (data?.holiday) {
        return <HolidayEvent holiday={data?.holiday} />;
      }

      // if (data?.blockout) {
      //   return <BlockoutEvent blockout={data?.blockout} />;
      // }

      return null;
    },
    timeSlotWrapper: ({
      children,
      value,
      resource,
    }: {
      children: JSX.Element;
      value: string;
      resource: number;
    }) => {
      return cloneElement(children, {
        onContextMenu: (e: MouseEvent) => {
          e.preventDefault();
          setContextMenuInfo({
            xPosition: e.clientX,
            yPosition: e.clientY,
            selectedTime: value,
            resourceId: resource,
          });
        },
      });
    },
  };

  const STEP = 5;
  const TIME_SLOTS = 60 / STEP;

  return (
    <div className="flex flex-col md:h-[calc(100%-56px)] h-[calc(100vh-56px)] gap-2 p-2">
      <div className="flex justify-between items-center ">
        <div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-[240px] pl-3 text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                {date ? format(date, "PPP") : <span>Pick a date</span>}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <ShadcnCalendar
                mode="single"
                selected={date}
                onSelect={(day) => {
                  setDate(day as Date);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex gap-4">
          <Button size="sm" onClick={onTodayClick}>
            Today
          </Button>

          <div className="flex flex-row items-center">
            <Button
              size="icon"
              className="w-7 h-8 rounded-tr-none rounded-br-none"
              variant="outline"
              onClick={onPrevClick}
            >
              <ChevronLeft />
            </Button>
            <div className="border px-3 h-8 flex items-center justify-center">
              {dateText}
            </div>
            <Button
              size="icon"
              className="w-7 h-8 rounded-tl-none rounded-bl-none"
              variant="outline"
              onClick={onNextClick}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>

        <Button asChild size="sm">
          <Link to="/employee/profile/attendance/requests">
            Attendance Requests List
          </Link>
        </Button>

        <div className="flex items-center gap-1">
          {VIEW_OPTIONS.map(({ id, label }) => {
            return (
              <Button key={id} size="sm" onClick={() => setView(id)}>
                {label}
              </Button>
            );
          })}
        </div>
      </div>

      <div
        className={cn(
          "flex-1 relative",
          timeSlotLinesMap[STEP as TimeSlotMinutes]
        )}
        onClick={() => {
          setContextMenuInfo(undefined);
        }}
      >
        <Calendar
          events={ATTENDANCE_EVENTS}
          defaultView={Views.MONTH}
          resources={view === Views.DAY ? RESOURCES : undefined}
          components={components}
          toolbar={false}
          date={date}
          view={view}
          onView={setView}
          onNavigate={setDate}
          step={STEP}
          timeslots={TIME_SLOTS}
        />
      </div>
    </div>
  );
};
